import { useMemo, type JSX } from 'react';
import { toast } from 'sonner';
import { Copy, Hash, Lightbulb, Loader2, Music, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { useSearchIdeas } from '@/hooks/useSearchIdeas';
import { useMe } from '@/hooks/useMe';
import { joinChannels } from '@/lib/outreach-copy';
import type { SearchIdea } from '@/api/types';
import { soundUrl } from '@/discovery/lib/platform';

/**
 * Example searches for outreachers.
 *
 * These are SUGGESTIONS, and the page says so in the loudest place available.
 * Outreachers find creators their own way, and a list handed down from admin
 * tooling reads as a scope unless it's explicitly framed otherwise — which
 * would be worse than showing nothing, because it would narrow what people try.
 *
 * The terms are the ones an admin approved in discovery, so they're the same
 * searches the team is actually running, kept current without anyone
 * maintaining a doc.
 */
export function SearchIdeasPage(): JSX.Element {
  const query = useSearchIdeas();
  const ideas = query.data?.ideas ?? [];
  const me = useMe();
  const team = me.data?.role === 'outreacher' ? me.data.team : null;
  // Where this team finds creators. Falls back to a neutral phrase when the
  // team (or its channel list) is unknown rather than assuming TikTok.
  const where = joinChannels(team?.allowedChannels ?? []) || 'social media';

  // Grouped by what prompted them, so "why am I being shown this" is answered
  // on the row rather than in a legend.
  const groups = useMemo(() => {
    const map = new Map<string, SearchIdea[]>();
    for (const i of ideas) {
      const key = i.sourceLabel || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(i);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [ideas]);

  function copy(idea: SearchIdea): void {
    const text = idea.searchMode === 'sound' ? soundLink(idea) : `#${idea.term}`;
    void navigator.clipboard.writeText(text);
    toast.success(`Copied ${text}`);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Search ideas"
        description={`Things you can search on ${where} to find creators worth messaging.`}
      />

      {/* Framing first, before any term is read. */}
      <div className="rounded-lg border border-brand/30 bg-brand/[0.06] px-4 py-3">
        <div className="flex items-start gap-2">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="space-y-1 text-sm">
            <p className="font-semibold text-foreground">
              Suggestions, not restrictions.
            </p>
            <p className="text-foreground/80">
              These are starting points based on the niches and campaigns we're pushing
              right now. Search however you like. If you find a better angle, use it.
              Nothing here limits who you can message or log.
            </p>
          </div>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading ideas…
        </div>
      ) : ideas.length === 0 ? (
        <EmptyState
          icon={<Search className="h-6 w-6" />}
          title="No search ideas yet."
          hint="They'll appear here once an admin approves some. Keep searching your own way in the meantime."
        />
      ) : (
        <div className="space-y-5">
          {groups.map(([label, items]) => (
            <section key={label} className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
              </h2>
              <Card>
                <CardContent className="divide-y divide-border/60 p-0">
                  {items.map((idea) => (
                    <div
                      key={idea.publicId}
                      className="flex items-center justify-between gap-3 px-4 py-2.5"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        {idea.searchMode === 'sound' ? (
                          <Music className="h-4 w-4 shrink-0 text-primary" />
                        ) : (
                          <Hash className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {idea.searchMode === 'sound' && idea.musicId ? (
                              <a
                                href={soundUrl('tiktok', idea.musicId)}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:underline"
                                title="Open this sound on TikTok"
                              >
                                {idea.term}
                              </a>
                            ) : idea.searchMode === 'sound' ? (
                              idea.term
                            ) : (
                              `#${idea.term}`
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {idea.searchMode === 'sound'
                              ? 'TikTok sound. Search it to find creators already using it'
                              : idea.reach
                                ? `${formatReach(idea.reach)} creators use this tag`
                                : 'Hashtag'}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copy(idea)}
                        title={idea.searchMode === 'sound' ? 'Copy the TikTok sound link' : `Copy to search on ${where}`}
                      >
                        <Copy className="mr-1 h-3.5 w-3.5" />
                        Copy
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sound suggestions carry a music id; rebuild the URL an outreacher can open. */
function soundLink(idea: SearchIdea): string {
  return idea.musicId ? soundUrl('tiktok', idea.musicId) : idea.term;
}

function formatReach(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}
